
import React, {
    PropsWithChildren,
    useState,
    useRef,
    HTMLAttributes,
    forwardRef,
} from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './ProductCardCompare.css';
import { TCreditCard } from '../../types/models/CreditCard';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useWidthContext } from '../../providers/Width';
import { Button, Row, Col, Title, Image, Section, Banner, CarouselSlider } from '../../importCL';
import { cleanInternalLinkUrl } from '../../utils/cob-utils';

interface IProductCardCompareProps extends MappedComponentProperties {
    removingPadding?:boolean;
    title?:string;
    annualFeeText?:string;
    familyCards?: TCreditCard[];
}

interface IArrowGroupProps extends HTMLAttributes<any> {
    slide?:number;
    count?:number;
    windowScreen?:number;
    previousArrowRef?: React.MutableRefObject<HTMLButtonElement>;
    nextArrowRef?: React.MutableRefObject<HTMLButtonElement>;
}

export const ProductCardCompareEditConfig = {
    emptyLabel: 'Product Card Compare',
    isEmpty: (props: PropsWithChildren<IProductCardCompareProps>) => {
        return !props || !props.title;
    },
};

// eslint-disable-next-line react/display-name
export const ArrowGroup = forwardRef(( props: IArrowGroupProps ) => {
    const slidesShown = props.windowScreen && props.windowScreen>=768 ? 3 : 2;
    const viewingMessage = <>Viewing <strong>{`${props.slide!==undefined && props.slide + 1} - ${props.slide!==undefined && props.slide + slidesShown}`}</strong> of <strong>{props.count}</strong></>;

    return (
        <Row align='center' className='ProductCardCompare__ArrowGroup mb-4'>
            <Col xs={1} md={2}>
                <button ref={props.previousArrowRef} 
                    className={`ProductCardCompare__ArrowGroup--left ${props.slide === 0 ? 'disabled' : '' }`}
                    disabled={(props.slide ?? 0) <= 0}
                    aria-label='Previous Cards'>
                    <span className='arrowLeft'/>
                </button>
            </Col>
            <Col>
                <p className='ProductCardCompare__ArrowGroup--text'>{viewingMessage}</p>
            </Col>
            <Col xs={1} md={2} >
                <button ref={props.nextArrowRef} 
                    className={`ProductCardCompare__ArrowGroup--right ${props.count && props.slide!==undefined && props.slide === props?.count - slidesShown?'disabled':''}`} 
                    disabled={props.slide === (props.count as number - slidesShown)}
                    aria-label='Next Cards'>
                    <span className='arrowRight'/>
                </button>
            </Col>
        </Row>
    );
});

const ProductCardCompare = (props: PropsWithChildren<IProductCardCompareProps>): JSX.Element => {
    const [slide, setSlide] = useState<number>(0);
    const numberOfCards=props.familyCards ? props.familyCards.length : 0;
    const  { width } = useWidthContext();
    const displayArrows: boolean = width >767 && numberOfCards > 3 || width <=767 && numberOfCards > 2;
    const nextArrowRef = useRef<HTMLButtonElement>(null);
    const previousArrowRef = useRef<HTMLButtonElement>(null);

    const handleEqualizeHeight = () => {
        ['.ProductCardCompare__banner', '.ProductCardCompare__cardContainer .Image', '.ProductCardCompare__productName', '.ProductCardCompare__rewards', '.ProductCardCompare__apr']
            .forEach((selector) => {
                const elements = Array.from(document.querySelectorAll(selector));
                const heights = elements.map((el: any) => {
                    // reset heights to get the true height
                    el.style.height = '';
                    return el.offsetHeight;
                });
                const maxHeight = Math.max(...heights, 0);

                elements.forEach((el: any) => {
                    el.style.height = maxHeight ? maxHeight + 'px' : '';
                });
            });
    };

    const getSlides = () => {
        if (!props.familyCards || props.familyCards.length < 1) {
            return [];
        }

        return props.familyCards?.map((card, index) => {
            if (!props.isInEditor && (card.aprLow === '0.0' || card.aprLow === '')) {
                window.location.assign(cleanInternalLinkUrl('/content/cob-corp-acquisition/us/en/404.html'));
            }
            return (
                <div key={index} className={`ProductCardCompare__slideContainer ${card.feeLow ? 'mt-12':'mt-4'}`}>
                    <div className='ProductCardCompare__slideColumn'>
                        <div className='ProductCardCompare__slideImage mb-4'>

                            <figure className='ProductCardCompare__cardContainer'>
                                <div
                                    className={`ProductCardCompare__banner ProductCardCompare__banner${card.feeLow === '0' ? '--height': ''}`}>
                                    {card.feeLow ==='0' &&
                                    <Banner className='ProductCardCompare__figure'>
                                        {props.annualFeeText ? transformHTMLParserLinks(props.annualFeeText): <>No Annual Fee<sup>†</sup></>}
                                    </Banner>
                                    }
                                </div>
                                <Image loading='lazy' src={card.imagePath} alt={card.altText}/>
                            </figure>

                        </div>
                        <a className='ProductCardCompare__detailsLink' href={card.cardDetailsLink}>See Card Detail</a>
                        <h3
                            className='ProductCardCompare__productName mt-6 mb-2'>
                            {transformHTMLParserLinks(card.productName)}
                        </h3>
                        <Button href={card.preQualifyLink} className='ProductCardCompare__cta py-2 px-4' size='medium'>
                            {width>=768 ? 'See If You Pre-Qualify':'Pre-Qualify'}
                        </Button>
                        <a className='ProductCardCompare__terms' href={card.staticLink || card.dynamicTermsLink}>
                            <small>
                                <sup>†</sup> Terms, fees & conditions
                            </small>
                        </a>
                        <Title tag='h4' className='ProductCardCompare__rewards-apr-title mt-10'>
                            <span>Rewards <sup>†</sup></span>
                        </Title>
                        <div className='ProductCardCompare__rewards mt-10' >
                            {card.cardRewards.map((reward, index) => {
                                return (
                                    <p key={index}>{transformHTMLParserLinks(reward)}</p>
                                );
                            })}
                        </div>
                        <Title tag='h4' className='ProductCardCompare__rewards-apr-title mt-10'>
                            <span>Rates & Fees <sup>†</sup></span>
                        </Title>
                        <div className='ProductCardCompare__apr mt-10' >
                            <p>APR is <strong>{transformHTMLParserLinks(card.aprLow)}%</strong>
                                <small>{transformHTMLParserLinks(card.aprText)}</small>
                            </p>
                            <p>Annual Fee is {transformHTMLParserLinks(card.annualFeeText)}</p>
                            {card.creditLineValue && <p>Credit Line is up to <strong>${transformHTMLParserLinks(card.creditLineValue)}</strong></p>}
                        </div>
                        <a className='ProductCardCompare__terms' href={card.staticLink || card.dynamicTermsLink}>
                            <small>
                                <sup>†</sup> Terms, fees & conditions
                            </small>
                        </a>
                        <hr/>
                    </div>
                </div>
            );
        });
    };

    const handleSlideChangeCallback = (_currentIndex: number, nextIndex: number) => {
        setSlide(nextIndex);
    };

    return (
        <Section id='product-card-compare' className={`ProductCardCompare ${props.removingPadding ? 'removePadding': ''}`} containerFluid={true}>
            <Title tag='h2' showHr align='center' className='mb-8'>{props.title}</Title>
            {displayArrows && (
                <ArrowGroup nextArrowRef={nextArrowRef} previousArrowRef={previousArrowRef} slide={ slide } count={numberOfCards} windowScreen={width}/>
            )}
            <CarouselSlider
                className='ProductCardCompare__slider'
                slidesToShow={width>= 768 ? 3 : 2}
                hideDefaultArrows={true}
                onLoad={() => handleEqualizeHeight()}
                customArrows={{
                    onNextCallback: handleSlideChangeCallback,
                    onPreviousCallback: handleSlideChangeCallback,
                    nextArrowRef: nextArrowRef,
                    previousArrowRef: previousArrowRef,
                }}
                slides={getSlides()} />
        </Section>
    );
};

export default ProductCardCompare;
